import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workouts, exercises as exercisesApi, clients } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import Modal, { ConfirmModal } from '../../components/ui/Modal'

const WORKOUT_TYPES = [
  { value: 'strength', label: 'Strength' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'hiit', label: 'HIIT' },
  { value: 'circuit', label: 'Circuit' },
  { value: 'full_body', label: 'Full Body' },
  { value: 'upper', label: 'Upper Body' },
  { value: 'lower', label: 'Lower Body' },
  { value: 'core', label: 'Core' },
  { value: 'flexibility', label: 'Flexibility' },
]

const DIFFICULTY_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]

function WorkoutForm({ workout, onSave, onCancel, isNew }) {
  const [name, setName] = useState(workout?.name || '')
  const [description, setDescription] = useState(workout?.description || '')
  const [workoutType, setWorkoutType] = useState(workout?.workoutType || 'strength')
  const [difficultyLevel, setDifficultyLevel] = useState(workout?.difficultyLevel || 'intermediate')
  const [durationMinutes, setDurationMinutes] = useState(workout?.durationMinutes || 45)

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      name,
      description,
      workoutType,
      difficultyLevel,
      durationMinutes: parseInt(durationMinutes) || 45,
      isActive: true,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-indices-medium mb-1">Name *</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-indices-medium mb-1">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-indices-medium mb-1">Type</label>
          <select value={workoutType} onChange={(e) => setWorkoutType(e.target.value)}
            className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal">
            {WORKOUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-indices-medium mb-1">Difficulty</label>
          <select value={difficultyLevel} onChange={(e) => setDifficultyLevel(e.target.value)}
            className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal">
            {DIFFICULTY_LEVELS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-indices-medium mb-1">Duration (minutes)</label>
        <input type="number" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} min="5" max="180"
          className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium hover:bg-dial-elevated">Cancel</button>
        <button type="submit" className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg hover:opacity-90 font-medium">{isNew ? 'Create Workout' : 'Save Changes'}</button>
      </div>
    </form>
  )
}

function AddExerciseModal({ isOpen, onClose, onAdd, templateId }) {
  const [search, setSearch] = useState('')
  const [selectedExercise, setSelectedExercise] = useState(null)
  const [sets, setSets] = useState('3')
  const [reps, setReps] = useState('10')
  const [restSeconds, setRestSeconds] = useState('60')
  const [notes, setNotes] = useState('')

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setSelectedExercise(null)
      setSets('3')
      setReps('10')
      setRestSeconds('60')
      setNotes('')
    }
  }, [isOpen])

  const { data: exerciseList } = useQuery({
    queryKey: ['exercises'],
    queryFn: exercisesApi.list,
    enabled: isOpen,
  })

  const filtered = exerciseList?.filter(ex =>
    ex.name?.toLowerCase().includes(search.toLowerCase()) ||
    ex.muscleGroup?.toLowerCase().includes(search.toLowerCase())
  ) || []

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedExercise) return
    onAdd({
      exerciseId: selectedExercise.id,
      sets: sets || null,
      reps: reps || null,
      restSeconds: restSeconds || null,
      notes: notes || null,
    })
    // Reset
    setSearch('')
    setSelectedExercise(null)
    setSets('3')
    setReps('10')
    setRestSeconds('60')
    setNotes('')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Exercise">
      <form onSubmit={handleSubmit} className="space-y-4">
        {!selectedExercise ? (
          <>
            <input type="text" placeholder="Search exercises..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" autoFocus />
            <div className="max-h-60 overflow-y-auto space-y-1">
              {filtered.slice(0, 20).map(ex => (
                <button key={ex.id} type="button" onClick={() => setSelectedExercise(ex)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-dial-elevated text-indices-bright">
                  <span className="font-medium">{ex.name}</span>
                  {ex.muscleGroup && <span className="text-xs text-indices-muted ml-2">{ex.muscleGroup}</span>}
                </button>
              ))}
              {filtered.length === 0 && <p className="text-center text-indices-muted py-4">No exercises found</p>}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between bg-dial-elevated p-3 rounded-lg">
              <div>
                <p className="font-medium text-lume-teal">{selectedExercise.name}</p>
                {selectedExercise.muscleGroup && <p className="text-xs text-indices-muted">{selectedExercise.muscleGroup}</p>}
              </div>
              <button type="button" onClick={() => setSelectedExercise(null)} className="text-indices-muted hover:text-indices-bright text-sm">Change</button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-indices-muted mb-1">Sets</label>
                <input type="number" value={sets} onChange={(e) => setSets(e.target.value)} min="1"
                  className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
              </div>
              <div>
                <label className="block text-xs text-indices-muted mb-1">Reps</label>
                <input type="text" value={reps} onChange={(e) => setReps(e.target.value)} placeholder="e.g. 8-12"
                  className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
              </div>
              <div>
                <label className="block text-xs text-indices-muted mb-1">Rest (s)</label>
                <input type="number" value={restSeconds} onChange={(e) => setRestSeconds(e.target.value)} min="0"
                  className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-indices-muted mb-1">Notes</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes"
                className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium">Cancel</button>
              <button type="submit" className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg font-medium">Add Exercise</button>
            </div>
          </>
        )}
      </form>
    </Modal>
  )
}

function EditExerciseModal({ isOpen, onClose, onSave, exerciseItem }) {
  const [sets, setSets] = useState(exerciseItem?.sets?.toString() || '')
  const [reps, setReps] = useState(exerciseItem?.reps || '')
  const [restSeconds, setRestSeconds] = useState(exerciseItem?.restSeconds?.toString() || '')
  const [notes, setNotes] = useState(exerciseItem?.notes || '')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      exerciseItemId: exerciseItem.id,
      sets: sets || null,
      reps: reps || null,
      restSeconds: restSeconds || null,
      notes: notes || null,
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit: ${exerciseItem?.exercise?.name || 'Exercise'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-indices-muted mb-1">Sets</label>
            <input type="number" value={sets} onChange={(e) => setSets(e.target.value)} min="1"
              className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
          </div>
          <div>
            <label className="block text-xs text-indices-muted mb-1">Reps</label>
            <input type="text" value={reps} onChange={(e) => setReps(e.target.value)} placeholder="e.g. 8-12"
              className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
          </div>
          <div>
            <label className="block text-xs text-indices-muted mb-1">Rest (s)</label>
            <input type="number" value={restSeconds} onChange={(e) => setRestSeconds(e.target.value)} min="0"
              className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-indices-muted mb-1">Notes</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium">Cancel</button>
          <button type="submit" className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg font-medium">Save</button>
        </div>
      </form>
    </Modal>
  )
}

function AssignWorkoutModal({ isOpen, onClose, onAssign, templateName }) {
  const [selectedClientId, setSelectedClientId] = useState('')
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().split('T')[0])
  const [notes, setNotes] = useState('')

  const { data: clientList } = useQuery({
    queryKey: ['clients'],
    queryFn: clients.list,
    enabled: isOpen,
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedClientId) return
    onAssign({
      clientId: parseInt(selectedClientId),
      scheduledDate,
      notes: notes || null,
    })
    setSelectedClientId('')
    setNotes('')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Assign: ${templateName}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-indices-medium mb-1">Client *</label>
          <select value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)} required
            className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal">
            <option value="">Select client...</option>
            {(Array.isArray(clientList) ? clientList : []).map(c => (
              <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-indices-medium mb-1">Scheduled Date</label>
          <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
            className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
        </div>
        <div>
          <label className="block text-sm font-medium text-indices-medium mb-1">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Optional notes for the client"
            className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium">Cancel</button>
          <button type="submit" className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg font-medium">Assign</button>
        </div>
      </form>
    </Modal>
  )
}

export default function WorkoutDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isNew = !id || id === 'new'
  const [isEditing, setIsEditing] = useState(isNew)
  const [showAddExercise, setShowAddExercise] = useState(false)
  const [editingExercise, setEditingExercise] = useState(null)
  const [showAssign, setShowAssign] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [removeExerciseId, setRemoveExerciseId] = useState(null)

  const { data: workout, isLoading } = useQuery({
    queryKey: ['workout', id],
    queryFn: () => workouts.get(id),
    enabled: !isNew,
  })

  const createMutation = useMutation({
    mutationFn: workouts.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] })
      navigate(`/workouts/${data.id}`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => workouts.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] })
      queryClient.invalidateQueries({ queryKey: ['workout', id] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => workouts.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] })
      navigate('/workouts')
    },
  })

  const addExerciseMutation = useMutation({
    mutationFn: (data) => workouts.addExercise(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] })
      setShowAddExercise(false)
    },
  })

  const removeExerciseMutation = useMutation({
    mutationFn: (exerciseItemId) => workouts.removeExercise(id, exerciseItemId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workout', id] }),
  })

  const updateExerciseMutation = useMutation({
    mutationFn: (data) => workouts.updateExercise(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] })
      setEditingExercise(null)
    },
  })

  const assignMutation = useMutation({
    mutationFn: (data) => workouts.assign(id, data),
    onSuccess: () => setShowAssign(false),
  })

  const handleSave = (data) => {
    if (isNew) {
      createMutation.mutate(data)
    } else {
      updateMutation.mutate(data)
    }
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const handleRemoveExercise = (exerciseItemId) => {
    setRemoveExerciseId(exerciseItemId)
  }

  // Sort exercises by orderIndex
  const sortedExercises = workout?.exercises
    ? [...workout.exercises].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
    : []

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dial-black">
        <PageHeader title="Workout" showBack backTo="/workouts" />
        <div className="p-4"><div className="animate-pulse bg-dial-surface rounded-xl p-6 h-48" /></div>
      </div>
    )
  }

  if (!isNew && !workout) {
    return (
      <div className="min-h-screen bg-dial-black">
        <PageHeader title="Workout" showBack backTo="/workouts" />
        <div className="p-4 text-center py-12"><p className="text-indices-muted">Workout not found</p></div>
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="min-h-screen bg-dial-black">
        <PageHeader title={isNew ? 'New Workout' : 'Edit Workout'} showBack backTo="/workouts" />
        <div className="px-4 py-4">
          <div className="bg-dial-surface rounded-xl p-4 border border-dial-border">
            <WorkoutForm workout={workout} onSave={handleSave}
              onCancel={() => isNew ? navigate('/workouts') : setIsEditing(false)} isNew={isNew} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dial-black">
      <PageHeader
        title={workout.name}
        showBack backTo="/workouts"
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowAssign(true)} className="px-3 py-1.5 bg-lume-teal text-dial-black rounded-lg text-sm font-medium hover:opacity-90">Assign</button>
            <button onClick={() => setIsEditing(true)} className="p-2 text-lume-teal hover:opacity-80" title="Edit">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button onClick={handleDelete} className="p-2 text-red-500 hover:text-red-400" title="Archive">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Details Card */}
        <div className="bg-dial-surface rounded-xl p-4 border border-dial-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 text-xs rounded-full bg-dial-elevated text-lume-teal border border-dial-border capitalize">
              {workout.workoutType?.replace('_', ' ')}
            </span>
            {workout.difficultyLevel && (
              <span className="px-2 py-1 text-xs rounded-full bg-dial-elevated text-indices-medium border border-dial-border capitalize">
                {workout.difficultyLevel}
              </span>
            )}
            {workout.durationMinutes && (
              <span className="text-sm text-indices-muted">{workout.durationMinutes} min</span>
            )}
          </div>
          {workout.description && (
            <p className="text-sm text-indices-medium">{workout.description}</p>
          )}
          {workout.rationale && (
            <div className="bg-lume-teal/5 border border-lume-teal/20 rounded-lg p-3">
              <p className="text-xs font-medium text-lume-teal mb-1">AI Rationale</p>
              <p className="text-sm text-indices-medium">{workout.rationale}</p>
            </div>
          )}
        </div>

        {/* Exercises */}
        <div className="bg-dial-surface rounded-xl border border-dial-border">
          <div className="px-4 py-3 border-b border-dial-border flex items-center justify-between">
            <h2 className="font-semibold text-indices-bright">Exercises ({sortedExercises.length})</h2>
            <button onClick={() => setShowAddExercise(true)}
              className="px-3 py-1.5 bg-lume-teal text-dial-black rounded-lg text-sm font-medium hover:opacity-90">
              + Add
            </button>
          </div>
          {sortedExercises.length > 0 ? (
            <div className="divide-y divide-dial-border">
              {sortedExercises.map((item, index) => (
                <div key={item.id || index} className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-dial-elevated/50 transition-colors" onClick={() => setEditingExercise(item)}>
                  <span className="text-xs text-indices-muted w-6 text-center">{index + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-indices-bright truncate">{item.exercise?.name || 'Unknown'}</p>
                    <p className="text-sm text-indices-muted">
                      {item.sets && `${item.sets} sets`}
                      {item.reps && ` x ${item.reps}`}
                      {item.restSeconds && ` · ${item.restSeconds}s rest`}
                    </p>
                    {item.notes && <p className="text-xs text-indices-muted mt-0.5">{item.notes}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setEditingExercise(item); }} className="p-2.5 text-indices-muted hover:text-lume-teal rounded-lg hover:bg-dial-elevated transition-colors" title="Edit">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleRemoveExercise(item.id); }} className="p-2.5 text-indices-muted hover:text-red-500 rounded-lg hover:bg-dial-elevated transition-colors" title="Remove">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-indices-muted mb-3">No exercises yet</p>
              <button onClick={() => setShowAddExercise(true)}
                className="px-4 py-2 bg-lume-teal text-dial-black rounded-lg text-sm font-medium">
                Add First Exercise
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddExerciseModal
        isOpen={showAddExercise}
        onClose={() => setShowAddExercise(false)}
        onAdd={(data) => addExerciseMutation.mutate(data)}
        templateId={id}
      />

      {editingExercise && (
        <EditExerciseModal
          key={editingExercise.id}
          isOpen={!!editingExercise}
          onClose={() => setEditingExercise(null)}
          onSave={(data) => updateExerciseMutation.mutate(data)}
          exerciseItem={editingExercise}
        />
      )}

      <AssignWorkoutModal
        isOpen={showAssign}
        onClose={() => setShowAssign(false)}
        onAssign={(data) => assignMutation.mutate(data)}
        templateName={workout?.name}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false)
          deleteMutation.mutate()
        }}
        title="Delete Workout"
        message="Are you sure you want to delete this workout? This cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      <ConfirmModal
        isOpen={!!removeExerciseId}
        onClose={() => setRemoveExerciseId(null)}
        onConfirm={() => {
          removeExerciseMutation.mutate(removeExerciseId)
          setRemoveExerciseId(null)
        }}
        title="Remove Exercise"
        message="Remove this exercise from the workout?"
        confirmText="Remove"
        variant="danger"
      />
    </div>
  )
}
